import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Input, Form, Select, Empty, DatePicker } from "antd";
import dayjs from "dayjs";
import { getCompanySalesReps } from "../../../../../services/userService";
import { AUTHORIZE_NET_COMPANY } from "../../../../../utils/constants";
import { useMessageApi } from "../../../../../shared/providers/MessageProvider";
import { RATE_REVIEW_CONSTANTS } from "../../../constants/rateReviewConstants";
import { getRateReviewAttachments } from "../../../services/rateReviewApiService";
import useBodyScrollLock from "../../../../../hooks/useBodyScrollLock";
import FileViewerModal from "../FileViewerModal/FileViewerModal";
import { ENDPOINTS } from "../../../../../api/endpoints";
import api from "../../../../../api/config";
import iconEditGreen from "../../../../../assets/images/icon-edit-green.png";
import iconAddNotes from "../../../../../assets/images/icon-add-notes.svg";
import "../../../../Modal/BusinessModal.scss";
import "../shared/sharedModalStyles.scss";
import "./UpdateLeadModal.scss";

const { TextArea: AntdTextArea } = Input;

/**
 * Update Lead Modal Component
 *
 * Modal for updating rate review lead information
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is visible
 * @param {Function} props.onCancel - Callback when modal is closed
 * @param {Object} props.selectedLead - The lead object being edited
 * @param {Object} props.form - Ant Design form instance
 * @param {Function} props.onFinish - Callback when form is submitted
 * @param {boolean} props.loading - Whether the update is in progress
 * @param {boolean} props.canEdit - Whether user has permission to edit (defaults to true for backward compatibility)
 * @returns {JSX.Element} Update Lead Modal UI
 */
const UpdateLeadModal = ({
  open,
  onCancel,
  selectedLead,
  form,
  onFinish,
  onFileOpen,
  saveTick,
  loading = false,
  canEdit = true,
}) => {
  const message = useMessageApi();
  const [salesReps, setSalesReps] = useState([]);
  const [isLoadingSalesReps, setIsLoadingSalesReps] = useState(false);
  const [hasLoadedSalesReps, setHasLoadedSalesReps] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
  const [isEditingAppInfo, setIsEditingAppInfo] = useState(false);
  const [isEditingMerchantInfo, setIsEditingMerchantInfo] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const initialFormValuesRef = useRef(null);
  const [dateSubmitted, setDateSubmitted] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");
  const hasAutoUpdatedStatusRef = useRef(false);
  const [viewingFile, setViewingFile] = useState(null);
  const isSavingSavingsRef = useRef(false);
  const isSavingAppInfoRef = useRef(false);
  const isSavingMerchantInfoRef = useRef(false);
  const isSavingNotesRef = useRef(false);
  const [shouldShowFinalStepCompleted, setShouldShowFinalStepCompleted] = useState(false);

  // Lock body scroll when modal is open
  useBodyScrollLock(open);

  // Lazy load sales reps when dropdown is opened/focused (like ExportModal)
  const loadSalesRepsIfNeeded = async () => {
    if (isLoadingSalesReps || hasLoadedSalesReps) return;

    setIsLoadingSalesReps(true);
    try {
      // Direct API call to fetch Authorize.net company users (same as ExportModal)
      const allUsers = await getCompanySalesReps(AUTHORIZE_NET_COMPANY);

      // Use all users from API (same as ExportModal - no role_id filtering)
      // API already filters by company, so we get all Authorize.net users
      let authorizeNetReps = Array.isArray(allUsers) ? allUsers : [];

      // Sort alphabetically by name
      authorizeNetReps.sort((a, b) => {
        const nameA = (a.name || "").trim();
        const nameB = (b.name || "").trim();
        return nameA.localeCompare(nameB);
      });

      setSalesReps(authorizeNetReps || []);
      setHasLoadedSalesReps(true);
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to load sales representatives";
      message.error(errorMessage, 2.5);
      setSalesReps([]);
      // Allow retry on next dropdown open if the request failed
      setHasLoadedSalesReps(false);
    } finally {
      setIsLoadingSalesReps(false);
    }
  };

  // Fetch attachments when modal opens
  useEffect(() => {
    const fetchAttachments = async () => {
      if (!open || !selectedLead) {
        setAttachments([]);
        return;
      }

      const zohoId = selectedLead.id || selectedLead.Id;
      if (!zohoId) {
        setAttachments([]);
        return;
      }

      setIsLoadingAttachments(true);
      try {
        const response = await getRateReviewAttachments(zohoId);
        if (response?.success) {
          setAttachments(response.data || []);
        } else {
          setAttachments([]);
        }
      } catch (error) {
        // Silently handle attachment fetch errors - empty state is acceptable
        setAttachments([]);
      } finally {
        setIsLoadingAttachments(false);
      }
    };

    fetchAttachments();
  }, [open, selectedLead]);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setSalesReps([]);
      setHasLoadedSalesReps(false);
      setAttachments([]);
      initialFormValuesRef.current = null;
      setDateSubmitted("");
      setLastUpdated("");
      hasAutoUpdatedStatusRef.current = false;
      isSavingSavingsRef.current = false;
      isSavingAppInfoRef.current = false;
      isSavingMerchantInfoRef.current = false;
      isSavingNotesRef.current = false;
      setShouldShowFinalStepCompleted(false);
    }
  }, [open]);

  // Exit edit modes when canEdit becomes false
  useEffect(() => {
    if (!canEdit) {
      setIsEditingAppInfo(false);
      setIsEditingMerchantInfo(false);
      setIsAddingNote(false);
    }
  }, [canEdit]);

  // Normalize status string for comparison
  const normalizeStatus = useCallback((s) =>
    String(s || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " "), []);

  // Function to update status to "Review Being Analyzed" when file is reviewed/viewed
  const updateStatusOnFileReview = useCallback(() => {
    if (!canEdit) return;
    // Auto-update status if not already updated
    if (
      hasAutoUpdatedStatusRef.current ||
      !selectedLead ||
      !onFinish ||
      !form
    ) {
      return;
    }

    const currentStatus =
      selectedLead?.Lead_Status || selectedLead?.status || "";
    const currentStatusNormalized = normalizeStatus(currentStatus);
    const targetStatus = "Review Being Analyzed";
    const targetStatusNormalized = normalizeStatus(targetStatus);

    // Only update if current status is not already "Review Being Analyzed" or a later status
    // Status progression: "Review Info Received" -> "Review Being Analyzed" -> "Calculating Savings" -> "Schedule a Call to discuss"
    const statusSteps = [
      "review info received",
      "review being analyzed",
      "calculating savings",
      "schedule a call to discuss",
    ];

    const currentIndex = statusSteps.findIndex(
      (s) => s === currentStatusNormalized
    );
    const targetIndex = statusSteps.findIndex(
      (s) => s === targetStatusNormalized
    );

    // If current status is already at or beyond target status, don't update
    if (currentIndex >= targetIndex && currentIndex >= 0) {
      return;
    }

    // Mark as updated to prevent duplicate updates
    hasAutoUpdatedStatusRef.current = true;

    // Update form field value
    form.setFieldsValue({ Lead_Status: targetStatus });

    // Call onFinish to persist the change to Zoho backend
    onFinish(
      { Lead_Status: targetStatus },
      { keepOpen: true, source: "file-reviewed-status-update" }
    );
  }, [canEdit, selectedLead, onFinish, form, normalizeStatus]);

  // Handle file click - open file viewer (allowed in view mode; status-update action remains restricted)
  const handleFileClick = async (attachment) => {
    const zohoId = selectedLead?.id || selectedLead?.Id;
    if (!zohoId || !attachment?.id) return;

    // Build view URL for Zoho attachment (inline viewing, not download)
    // Use api.getUri() to construct full URL using api instance (same pattern as other components)
    const viewUrl = api.getUri({
      url: ENDPOINTS.VIEW_ATTACHMENT(zohoId, attachment.id),
    });

    // Set viewing file to open FileViewerModal
    setViewingFile({
      fileName: attachment.fileName || attachment.file_name || "File",
      downloadUrl: viewUrl,
      fileType: attachment.fileType || attachment.file_type || "",
    });

    // Call the parent's onFileOpen callback if provided
    if (onFileOpen) {
      onFileOpen(attachment);
    }
  };

  // Update status when file is actually viewed/reviewed (when FileViewerModal opens and file loads)
  useEffect(() => {
    // Only update status when a file is actually being viewed
    if (viewingFile && !hasAutoUpdatedStatusRef.current) {
      // Update status when file viewer opens (file is being reviewed)
      updateStatusOnFileReview();
    }
  }, [viewingFile, updateStatusOnFileReview]);

  // Close file viewer
  const handleCloseFileViewer = () => {
    setViewingFile(null);
  };

  // Get sales rep options for Select - Same format as ExportModal
  const getSalesRepOptions = () => {
    if (!Array.isArray(salesReps) || salesReps.length === 0) {
      return [];
    }

    // Map sales reps to Select options format (same as ExportModal)
    return salesReps
      .filter((rep) => rep && rep.name) // Filter out invalid entries
      .map((rep) => ({
        value: rep.name || "",
        label: rep.name || "",
      }));
  };

  // Match BusinessModal search behavior
  const filterOption = (input, option) =>
    String(option?.label || "")
      .toLowerCase()
      .includes(String(input || "").toLowerCase());

  const businessName = useMemo(() => {
    if (!selectedLead) return "Update Rate Review Lead";
    return (
      selectedLead.Company ||
      selectedLead.businessName ||
      selectedLead.company ||
      "Update Rate Review Lead"
    );
  }, [selectedLead]);

  const leadStatus = useMemo(() => {
    if (!selectedLead) return "";
    return selectedLead.Lead_Status || selectedLead.status || "";
  }, [selectedLead]);

  // Watch Lead_Status so UI (checkbox/stepper) updates when setFieldsValue is called
  const watchedLeadStatus = Form.useWatch("Lead_Status", form);
  const watchedSavings = Form.useWatch("Savings", form);

  /**
   * Status steps for UI stepper
   * Uses constants from RATE_REVIEW_CONSTANTS
   */
  const statusSteps = useMemo(() => RATE_REVIEW_CONSTANTS.STATUS_STEPS, []);

  const statusStepIndex = useMemo(() => {
    const current = normalizeStatus(leadStatus);
    const idx = statusSteps.findIndex(
      (s) => normalizeStatus(s.value) === current
    );
    if (idx >= 0) return idx;

    // tolerate common variants from Zoho/formatting
    const alias = (() => {
      if (!current) return "";
      if (current === "review being analyzed") return "review being analyzed"; // label differs from value casing
      if (current === "review being analysed") return "review being analyzed";
      if (current === "schedule a call") return "schedule a call to discuss";
      if (current === "schedule a call to discuss")
        return "schedule a call to discuss";
      return current;
    })();
    const idx2 = statusSteps.findIndex(
      (s) => normalizeStatus(s.value) === alias
    );
    return idx2 >= 0 ? idx2 : 0;
  }, [statusSteps, leadStatus, normalizeStatus]);

  // Helper function to build form values from selectedLead
  const buildFormValues = useCallback((lead) => {
    if (!lead) return {};
    const merchantFullName = `${lead?.First_Name || ""} ${
      lead?.Last_Name || ""
    }`.trim();
    return {
      Sales_Agent: lead?.Sales_Agent || "",
      Gateway_ID: lead?.Gateway_ID || "",
      Account_Rep: lead?.Account_Rep || "",
      Merchant_Name: merchantFullName,
      Phone: lead?.Phone || "",
      Email: lead?.Email || "",
      Notes1: lead?.Notes1 || "",
      Lead_Status: lead?.Lead_Status || lead?.status || "",
      Savings: lead?.Savings ?? lead?.savings ?? "",
      First_Name: lead?.First_Name || "",
      Last_Name: lead?.Last_Name || "",
      Company: lead?.Company || lead?.businessName || lead?.company || "",
    };
  }, []);

  // Helper function to sync dates from selectedLead
  const syncDates = useCallback((lead) => {
    setDateSubmitted(
      lead?.Created_Time ? lead.Created_Time.split("T")[0] : ""
    );
    setLastUpdated(
      lead?.Last_Activity_Time
        ? lead.Last_Activity_Time.split("T")[0]
        : ""
    );
  }, []);

  // When final step (step 4, index 3: "Schedule a Call to discuss") is active, 
  // show completed state after 5 seconds delay
  useEffect(() => {
    const activeStep = statusStepIndex;
    if (activeStep === 3) {
      const timer = setTimeout(() => {
        setShouldShowFinalStepCompleted(true);
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setShouldShowFinalStepCompleted(false);
    }
  }, [statusStepIndex]);

  // Capture initial values so we can "cancel" edits by toggling pencil off
  useEffect(() => {
    if (!open || !form || initialFormValuesRef.current) return;
    // Ensure newly introduced editable fields have defaults before snapshotting
    const defaults = buildFormValues(selectedLead);
    // Only fill defaults when field is empty, so we don't overwrite parent-provided values
    const existing = form.getFieldsValue(true) || {};
    const next = { ...existing };
    Object.entries(defaults).forEach(([key, value]) => {
      const cur = next[key];
      if (cur === undefined || cur === null || cur === "") {
        next[key] = value;
      }
    });
    form.setFieldsValue(next);
    initialFormValuesRef.current = form.getFieldsValue(true);
  }, [open, form, selectedLead, buildFormValues]);

  // Sync form values and dates from selectedLead
  const syncFormFromLead = useCallback((lead, updateInitial = false) => {
    if (!lead || !form) return;
    const updatedValues = buildFormValues(lead);
    form.setFieldsValue(updatedValues);
    syncDates(lead);
    if (updateInitial) {
      initialFormValuesRef.current = form.getFieldsValue(true);
    } else if (initialFormValuesRef.current) {
      initialFormValuesRef.current = {
        ...initialFormValuesRef.current,
        ...updatedValues,
      };
    }
  }, [form, buildFormValues, syncDates]);

  // When parent confirms a successful save while modal stays open, refresh the "initial" snapshot
  // so cancel/revert reflects the latest saved values, and sync form with updated selectedLead
  useEffect(() => {
    if (!open || !form || !saveTick) return;
    syncFormFromLead(selectedLead, true);
    // After a confirmed save, exit edit modes so UI reflects saved state
    setIsEditingAppInfo(false);
    setIsEditingMerchantInfo(false);
    setIsAddingNote(false);
  }, [saveTick, open, form, selectedLead, syncFormFromLead]);

  // Sync all fields from selectedLead to form when selectedLead changes (e.g., after successful save)
  // This ensures the modal reflects the updated values without needing to close/reopen
  // Only sync when not in edit mode to avoid overwriting user's current edits
  useEffect(() => {
    if (!open || !selectedLead || !form) return;
    // Don't sync if user is currently editing - let saveTick effect handle sync after save
    if (isEditingAppInfo || isEditingMerchantInfo || isAddingNote) return;
    syncFormFromLead(selectedLead, false);
  }, [open, selectedLead, form, isEditingAppInfo, isEditingMerchantInfo, isAddingNote, syncFormFromLead]);

  const replaceIsoDate = (iso, yyyyMmDd) => {
    const nextDate = String(yyyyMmDd || "").trim();
    if (!nextDate) return "";
    const s = String(iso || "").trim();
    // If we have an ISO-like string "YYYY-MM-DDT...", preserve time/timezone portion
    if (s.length >= 11 && s[10] === "T") {
      return `${nextDate}${s.slice(10)}`;
    }
    return nextDate;
  };

  const closeAndReset = () => {
    // Don't close UpdateLeadModal if FileViewerModal is open
    if (viewingFile) {
      return;
    }
    setIsEditingAppInfo(false);
    setIsEditingMerchantInfo(false);
    setIsAddingNote(false);
    onCancel?.();
  };

  const resetToInitial = (keys = []) => {
    if (!form) return;
    const initial = initialFormValuesRef.current || {};
    if (!Array.isArray(keys) || keys.length === 0) {
      form.setFieldsValue(initial);
      return;
    }
    const next = {};
    keys.forEach((k) => {
      next[k] = initial[k];
    });
    form.setFieldsValue(next);
  };

  const getFileKind = (fileName = "", fileType = "") => {
    const name = String(fileName || "").toLowerCase();
    const type = String(fileType || "").toLowerCase();
    if (type.includes("pdf") || name.endsWith(".pdf")) return "pdf";
    if (
      type.startsWith("image/") ||
      [".png", ".jpg", ".jpeg", ".webp", ".gif", ".bmp", ".svg"].some((ext) =>
        name.endsWith(ext)
      )
    )
      return "image";
    return "file";
  };

  const handleSavingsSave = () => {
    if (!form) return;
    if (loading) return;
    if (!canEdit) return;

    const raw = watchedSavings;
    const value = String(raw ?? "").trim();
    if (!value) {
      message.warning("Please enter a Savings value first", 2);
      return;
    }

    const scheduleChecked = watchedLeadStatus === "Schedule a Call to discuss";
    const payload = { Savings: value };
    if (scheduleChecked) {
      payload.Lead_Status = "Schedule a Call to discuss";
    }

    // Set flag to track Savings save in progress
    isSavingSavingsRef.current = true;

    // keepOpen so modal doesn't close when saving Savings via the icon
    onFinish?.(payload, { keepOpen: true, source: "savings" });
  };

  // Reset loading flags when loading becomes false and exit edit modes
  useEffect(() => {
    if (!loading) {
      // Exit edit modes for sections that were just saved
      if (isSavingAppInfoRef.current) {
        isSavingAppInfoRef.current = false;
        setIsEditingAppInfo(false);
      }
      if (isSavingMerchantInfoRef.current) {
        isSavingMerchantInfoRef.current = false;
        setIsEditingMerchantInfo(false);
      }
      if (isSavingNotesRef.current) {
        isSavingNotesRef.current = false;
        setIsAddingNote(false);
      }
      if (isSavingSavingsRef.current) {
        isSavingSavingsRef.current = false;
      }
    }
  }, [loading]);

  const PdfIcon = ({ className = "" }) => (
    <svg
      className={className}
      width="44"
      height="44"
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Document (thick dark outline + white folded corner like screenshot) */}
      <path
        d="M12 6H42L56 20V58H12V6Z"
        fill="#ffffff"
        stroke="#3B3B3B"
        strokeWidth="4"
        strokeLinejoin="round"
      />
      {/* Folded corner */}
      <path
        d="M42 6V20H56L42 6Z"
        fill="#ffffff"
        stroke="#3B3B3B"
        strokeWidth="4"
        strokeLinejoin="round"
      />

      {/* PDF ribbon on the left */}
      <path d="M6 14H30V28H6V14Z" fill="#E53935" />
      <text
        x="18"
        y="24"
        textAnchor="middle"
        fontSize="12"
        fontFamily="Arial"
        fontWeight="800"
        fill="#ffffff"
      >
        PDF
      </text>

      {/* Text lines */}
      <path
        d="M22 30H46"
        stroke="#3B3B3B"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path
        d="M22 38H46"
        stroke="#3B3B3B"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path
        d="M22 46H46"
        stroke="#3B3B3B"
        strokeWidth="4"
        strokeLinecap="round"
      />

      {/* Download badge bottom-right */}
      <circle cx="50" cy="52" r="10" fill="#3B3B3B" />
      <path
        d="M50 46V55"
        stroke="#ffffff"
        strokeWidth="3.2"
        strokeLinecap="round"
      />
      <path
        d="M45.5 52.5L50 57L54.5 52.5"
        stroke="#ffffff"
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const PdfIconSkeleton = ({ className = "" }) => (
    <svg
      className={className}
      width="44"
      height="44"
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M12 6H42L56 20V58H12V6Z"
        fill="#ffffff"
        stroke="#D6D9DD"
        strokeWidth="4"
        strokeLinejoin="round"
      />
      <path
        d="M42 6V20H56L42 6Z"
        fill="#ffffff"
        stroke="#D6D9DD"
        strokeWidth="4"
        strokeLinejoin="round"
      />
      <path d="M6 14H30V28H6V14Z" fill="#E9ECEF" />
      <path
        d="M22 30H46"
        stroke="#D6D9DD"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path
        d="M22 38H46"
        stroke="#D6D9DD"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path
        d="M22 46H46"
        stroke="#D6D9DD"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <circle cx="50" cy="52" r="10" fill="#D6D9DD" />
      <path
        d="M50 46V55"
        stroke="#ffffff"
        strokeWidth="3.2"
        strokeLinecap="round"
      />
      <path
        d="M45.5 52.5L50 57L54.5 52.5"
        stroke="#ffffff"
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const ImageIcon = ({ className = "" }) => (
    <svg
      className={className}
      width="44"
      height="44"
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Classic placeholder like attached image: thick rounded border + dot + mountain */}
      <rect
        x="10"
        y="14"
        width="44"
        height="36"
        rx="6"
        fill="none"
        stroke="#8F98A1"
        strokeWidth="5"
        strokeLinejoin="round"
      />
      <circle
        cx="23"
        cy="25"
        r="4.2"
        fill="none"
        stroke="#8F98A1"
        strokeWidth="5"
      />
      <path
        d="M16 46L28 34L36 42L44 34L52 46"
        stroke="#8F98A1"
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const ImageIconSkeleton = ({ className = "" }) => (
    <svg
      className={className}
      width="44"
      height="44"
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect
        x="10"
        y="14"
        width="44"
        height="36"
        rx="6"
        fill="#ffffff"
        stroke="#D6D9DD"
        strokeWidth="5"
        strokeLinejoin="round"
      />
      <circle
        cx="23"
        cy="25"
        r="4.2"
        fill="none"
        stroke="#D6D9DD"
        strokeWidth="5"
      />
      <path
        d="M16 46L28 34L36 42L44 34L52 46"
        stroke="#D6D9DD"
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const SaveCircleIcon = ({ className = "" }) => (
    <svg
      className={className}
      width="28"
      height="28"
      viewBox="0 0 28 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="14" cy="14" r="12" fill="#10B981" />
      <path
        d="M8.7 14.2l3.1 3.2L19.6 9.8"
        stroke="#ffffff"
        strokeWidth="2.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const CloseCircleIcon = ({ className = "" }) => (
    <svg
      className={className}
      width="28"
      height="28"
      viewBox="0 0 28 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="14" cy="14" r="12" fill="#EF4444" />
      <path
        d="M9.8 9.8l8.4 8.4M18.2 9.8l-8.4 8.4"
        stroke="#ffffff"
        strokeWidth="2.8"
        strokeLinecap="round"
      />
    </svg>
  );

  // SaveButton component that shows spinner when loading
  const SaveButton = ({ isLoading = false }) => {
    if (isLoading) {
      return (
        <div
          style={{
            width: "28px",
            height: "28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="simple-spinner"
            style={{
              width: "20px",
              height: "20px",
              border: "2px solid #e5f7ed",
              borderTop: "2px solid #10B981",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
        </div>
      );
    }
    return <SaveCircleIcon />;
  };

  if (!open) return null;

  return (
    <div
      className="modal fade show businessModal rate-review-business-modal"
      style={{ display: "block" }}
      tabIndex="-1"
      aria-modal="true"
      role="dialog"
      onClick={(e) => {
        // Don't close UpdateLeadModal if FileViewerModal is open
        if (viewingFile) {
          e.stopPropagation();
          return;
        }
        closeAndReset();
      }}
    >
      <div
        className="modal-dialog modal-dialog-custom"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header border-0 align-items-center">
            <div
              className="d-flex align-items-center gap-3 rr-modal-header-left"
              style={{ minWidth: 0 }}
            >
              <h1
                className="modal-title mb-0"
                title={businessName}
                style={{
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {businessName}
              </h1>
              <div className="rr-progress-tracker">
                <div className="rr-outer-prog">
                  <div
                    className={`${
                      statusStepIndex === 1 ? "rr-step2active" : ""
                    } ${statusStepIndex === 2 ? "rr-step3active" : ""} ${
                      statusStepIndex === 3 ? "rr-step3active" : ""
                    } ${statusStepIndex === 3 ? "rr-step4on" : ""}`}
                  ></div>
                  {statusSteps.map((step, index) => {
                    const isCompleted4 =
                      statusStepIndex === 3 &&
                      index === 3 &&
                      shouldShowFinalStepCompleted;
                    // When step 4 has completed4, remove active class and add complete class
                    const isActive = index === statusStepIndex && !isCompleted4;
                    const isCompleted = index < statusStepIndex;
                    // When third step (index 2) is active, first step (index 0) gets completed1 class
                    // When fourth step (index 3) is active, first step keeps completed1 class
                    const isCompleted1 =
                      (statusStepIndex === 2 && index === 0) ||
                      (statusStepIndex === 3 && index === 0);
                    // When fourth step (index 3) is active, second step (index 1) gets completed2 class
                    const isCompleted2 = statusStepIndex === 3 && index === 1;
                    // When fourth step (index 3) is active, third step (index 2) gets completed3 class
                    const isCompleted3 = statusStepIndex === 3 && index === 2;
                    // When step 4 (index 3) is active and step 2 (index 1) is complete, add arrow2 class
                    // Once applied, arrow2 class persists
                    const hasArrow2 =
                      statusStepIndex >= 3 && index === 1 && isCompleted;
                    // When step 3 (index 2) or later is active, first step (index 0) gets arrow1 class
                    // Once applied, arrow1 class persists (keeps showing even when step 4 becomes active)
                    const hasArrow1 =
                      statusStepIndex >= 2 && index === 0 && isCompleted;
                    // When step 4 has completed4 class, third step (index 2) gets arrow3 class
                    // Once applied, arrow3 class persists
                    const hasArrow3 =
                      shouldShowFinalStepCompleted &&
                      statusStepIndex === 3 &&
                      index === 2;
                    // First step should always keep complete class when completed
                    // Step 4 should get complete class when it has completed4
                    const shouldHaveComplete =
                      isCompleted ||
                      (index === 0 && isCompleted1) ||
                      isCompleted4;

                    // Build className array to ensure all classes are applied
                    const classNames = [
                      "rr-progress-step",
                      isActive && "rr-active",
                      shouldHaveComplete && "rr-complete",
                      isCompleted1 && "rr-completed1",
                      isCompleted2 && "rr-completed2",
                      isCompleted3 && "rr-completed3",
                      isCompleted4 && "rr-completed4",
                      hasArrow1 && "rr-arrow1",
                      hasArrow2 && "rr-arrow2",
                      hasArrow3 && "rr-arrow3",
                    ]
                      .filter(Boolean)
                      .join(" ");

                    return (
                      <div key={step.value} className={classNames}>
                        {step.label}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={closeAndReset}
            />
          </div>

          <div className="modal-body">
            {!canEdit && (
              <div className="alert alert-info permission-alert mb-4 py-2 px-3">
                You can view this lead. Editing is restricted.
              </div>
            )}
            {!selectedLead ? (
              <div className="p-3">No lead selected.</div>
            ) : (
              <Form
                form={form}
                layout="vertical"
                onFinish={(values) => {
                  if (!canEdit) return;
                  // Use full form store to avoid missing fields (even if a field isn't currently rendered)
                  const storeValues = form?.getFieldsValue?.(true) || {};
                  const next = { ...storeValues, ...values };

                  // Merchant Name is an editable display field; map to First/Last before submitting
                  if (typeof next.Merchant_Name === "string") {
                    const parts = next.Merchant_Name.trim()
                      .split(/\s+/)
                      .filter(Boolean);
                    next.First_Name = parts[0] || "";
                    next.Last_Name = parts.slice(1).join(" ");
                    delete next.Merchant_Name;
                  }

                  // Include date fields from local DatePicker state (if user changed them)
                  if (dateSubmitted !== undefined) {
                    next.Created_Time = replaceIsoDate(
                      selectedLead?.Created_Time,
                      dateSubmitted
                    );
                  }
                  if (lastUpdated !== undefined) {
                    next.Last_Activity_Time = replaceIsoDate(
                      selectedLead?.Last_Activity_Time,
                      lastUpdated
                    );
                  }

                  // Only send changed keys (prevents unintended clearing of fields like Savings: "")
                  const initial = initialFormValuesRef.current || {};
                  const normalize = (v) => {
                    if (v === undefined || v === null) return v;
                    if (Array.isArray(v))
                      return v.map((x) =>
                        typeof x === "string" ? x.trim() : x
                      );
                    if (typeof v === "string") return v.trim();
                    return v;
                  };

                  const updates = {};
                  Object.keys(next).forEach((key) => {
                    const cur = normalize(next[key]);
                    const prev = normalize(initial[key]);

                    // Skip undefined/null (but keep empty string if it changed)
                    if (cur === undefined || cur === null) return;

                    const changed =
                      Array.isArray(cur) || Array.isArray(prev)
                        ? JSON.stringify(cur || []) !==
                          JSON.stringify(prev || [])
                        : cur !== prev;

                    if (changed) {
                      updates[key] = next[key];
                    }
                  });

                  // Keep modal open on any update (user closes via X/outside click)
                  onFinish?.(updates, {
                    keepOpen: true,
                    source: "update-lead-modal",
                  });
                }}
              >
                {/* Application Info */}
                <div className="info-section mb-4">
                  <div className="section-header mb-3 d-flex justify-content-between align-items-center">
                    <h5 className="section-title mb-0">
                      Application Info{" "}
                      {!isEditingAppInfo && canEdit && (
                        <img
                          src={iconEditGreen}
                          alt="edit"
                          className="edit-icon ms-2 icon-button-small icon-button-pointer"
                          onClick={() => {
                            setIsEditingAppInfo((v) => {
                              const next = !v;
                              if (!next)
                                resetToInitial([
                                  "Sales_Agent",
                                  "Gateway_ID",
                                  "Account_Rep",
                                ]);
                              return next;
                            });
                          }}
                        />
                      )}
                    </h5>
                    {isEditingAppInfo && (
                      <div className="rr-section-actions">
                        <button
                          type="button"
                          className="rr-section-action-btn rr-section-action-save"
                          onClick={() => {
                            if (loading || !canEdit) return;
                            isSavingAppInfoRef.current = true;
                            form.submit();
                          }}
                          disabled={loading || !canEdit}
                          title="Save"
                          aria-label="Save"
                        >
                          <SaveButton
                            isLoading={loading && isSavingAppInfoRef.current}
                          />
                        </button>
                        {!loading && (
                          <button
                            type="button"
                            className="rr-section-action-btn rr-section-action-cancel"
                            onClick={() => {
                              resetToInitial([
                                "Sales_Agent",
                                "Gateway_ID",
                                "Account_Rep",
                              ]);
                              setIsEditingAppInfo(false);
                            }}
                            title="Close"
                            aria-label="Close"
                          >
                            <CloseCircleIcon />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {isEditingAppInfo ? (
                    <div className="rr-appinfo-row">
                      <div className="rr-appinfo-col">
                        <label className="field-label">Date Submitted</label>
                        <DatePicker
                          className="w-100 agent-picker"
                          format="YYYY-MM-DD"
                          value={
                            dateSubmitted
                              ? dayjs(dateSubmitted, "YYYY-MM-DD")
                              : null
                          }
                          onChange={(d) =>
                            setDateSubmitted(d ? d.format("YYYY-MM-DD") : "")
                          }
                          disabled={!canEdit}
                        />
                      </div>

                      <div className="rr-appinfo-col">
                        <label className="field-label">Last Updated</label>
                        <DatePicker
                          className="w-100 agent-picker"
                          format="YYYY-MM-DD"
                          value={
                            lastUpdated
                              ? dayjs(lastUpdated, "YYYY-MM-DD")
                              : null
                          }
                          onChange={(d) =>
                            setLastUpdated(d ? d.format("YYYY-MM-DD") : "")
                          }
                          disabled={!canEdit}
                        />
                      </div>

                      <div className="rr-appinfo-col">
                        <label className="field-label">Sales Rep</label>
                        <Form.Item
                          name="Sales_Agent"
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            className="w-100"
                            size="large"
                            placeholder={
                              isLoadingSalesReps
                                ? "Loading agents…"
                                : "Sales Rep"
                            }
                            allowClear={false}
                            showSearch
                            optionFilterProp="label"
                            filterOption={filterOption}
                            onDropdownVisibleChange={(o) =>
                              o && loadSalesRepsIfNeeded()
                            }
                            loading={isLoadingSalesReps}
                            suffixIcon={
                              isLoadingSalesReps ? (
                                <span className="spinner-border spinner-border-sm text-success" />
                              ) : undefined
                            }
                            dropdownMatchSelectWidth={false}
                            virtual={true}
                            notFoundContent={
                              isLoadingSalesReps ? (
                                <div
                                  className="d-flex justify-content-center align-items-center py-2"
                                  style={{ color: "#198754" }}
                                >
                                  <span
                                    className="spinner-border spinner-border-sm text-success me-2"
                                    role="status"
                                  />
                                  Loading agents…
                                </div>
                              ) : null
                            }
                            options={getSalesRepOptions()}
                            disabled={!canEdit}
                          />
                        </Form.Item>
                      </div>

                      <div className="rr-appinfo-col">
                        <label className="field-label">Gateway ID</label>
                        <Form.Item
                          name="Gateway_ID"
                          style={{ marginBottom: 0 }}
                        >
                          <Input size="large" placeholder="Gateway ID" disabled={!canEdit} />
                        </Form.Item>
                      </div>

                      <div className="rr-appinfo-col">
                        <label className="field-label">Start Rep</label>
                        <Form.Item
                          name="Account_Rep"
                          style={{ marginBottom: 0 }}
                        >
                          <Input size="large" placeholder="Enter Start Rep" disabled={!canEdit} />
                        </Form.Item>
                      </div>
                    </div>
                  ) : (
                    <div className="row">
                      <div className="col-md-3 col-6 mb-3">
                        <label className="field-label">Date Submitted</label>
                        <div className="field-value">
                          <b>
                            {selectedLead?.Created_Time
                              ? selectedLead.Created_Time.split("T")[0]
                              : "-"}
                          </b>
                        </div>
                      </div>
                      <div className="col-md-3 col-6 mb-3">
                        <label className="field-label">Last Updated</label>
                        <div className="field-value">
                          <b>
                            {selectedLead?.Last_Activity_Time
                              ? selectedLead.Last_Activity_Time.split("T")[0]
                              : "-"}
                          </b>
                        </div>
                      </div>
                      <div className="col-md-3 col-6 mb-3">
                        <label className="field-label">Sales Rep</label>
                        <div className="field-value">
                          <b>{selectedLead?.Sales_Agent || "-"}</b>
                        </div>
                      </div>
                      <div className="col-md-2 col-6 mb-3">
                        <label className="field-label">Gateway ID</label>
                        <div className="field-value">
                          <b>{selectedLead?.Gateway_ID || "-"}</b>
                        </div>
                      </div>

                      <div className="col-md-1 col-6 mb-3">
                        <label className="field-label">Start Rep</label>
                        <div className="field-value">
                          <b>{selectedLead?.Account_Rep || "-"}</b>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Merchant Info */}
                <div className="info-section mb-4">
                  <div className="section-header mb-3 d-flex justify-content-between align-items-center">
                    <h5 className="section-title mb-0">
                      Merchant Info{" "}
                      {!isEditingMerchantInfo && canEdit && (
                        <img
                          src={iconEditGreen}
                          alt="edit"
                          className="edit-icon ms-2 icon-button-small icon-button-pointer"
                          onClick={() => {
                            setIsEditingMerchantInfo((v) => {
                              const next = !v;
                              if (!next)
                                resetToInitial([
                                  "Merchant_Name",
                                  "Phone",
                                  "Email",
                                ]);
                              return next;
                            });
                          }}
                        />
                      )}
                    </h5>
                    {isEditingMerchantInfo && (
                      <div className="rr-section-actions">
                        <button
                          type="button"
                          className="rr-section-action-btn rr-section-action-save"
                          onClick={() => {
                            if (loading || !canEdit) return;
                            isSavingMerchantInfoRef.current = true;
                            form.submit();
                          }}
                          disabled={loading || !canEdit}
                          title="Save"
                          aria-label="Save"
                        >
                          <SaveButton
                            isLoading={
                              loading && isSavingMerchantInfoRef.current
                            }
                          />
                        </button>
                        {!loading && (
                          <button
                            type="button"
                            className="rr-section-action-btn rr-section-action-cancel"
                            onClick={() => {
                              resetToInitial(["Merchant_Name", "Phone", "Email"]);
                              setIsEditingMerchantInfo(false);
                            }}
                            title="Close"
                            aria-label="Close"
                          >
                            <CloseCircleIcon />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {isEditingMerchantInfo ? (
                    <div className="rr-appinfo-row">
                      <div className="rr-appinfo-col">
                        <label className="field-label">Merchant Name</label>
                        <Form.Item
                          name="Merchant_Name"
                          style={{ marginBottom: 0 }}
                        >
                          <Input size="large" placeholder="Merchant Name" disabled={!canEdit} />
                        </Form.Item>
                      </div>

                      <div className="rr-appinfo-col">
                        <label className="field-label">Merchant Phone</label>
                        <Form.Item name="Phone" style={{ marginBottom: 0 }}>
                          <Input size="large" placeholder="Merchant Phone" disabled={!canEdit} />
                        </Form.Item>
                      </div>

                      <div className="rr-appinfo-col">
                        <label className="field-label">Merchant Email</label>
                        <Form.Item name="Email" style={{ marginBottom: 0 }}>
                          <Input size="large" placeholder="Merchant Email" disabled={!canEdit} />
                        </Form.Item>
                      </div>
                    </div>
                  ) : (
                    <div className="row">
                      <div className="col-md-3 col-6 mb-3">
                        <label className="field-label">Merchant Name</label>
                        <div className="field-value">
                          <b>
                            {`${selectedLead?.First_Name || ""} ${
                              selectedLead?.Last_Name || ""
                            }`.trim() || "-"}
                          </b>
                        </div>
                      </div>
                      <div className="col-md-3 col-6 mb-3">
                        <label className="field-label">Merchant Phone</label>
                        <div className="field-value">
                          <b>{selectedLead?.Phone || "-"}</b>
                        </div>
                      </div>
                      <div className="col-md-3 col-6 mb-3">
                        <label className="field-label">Merchant Email</label>
                        <div className="field-value">
                          <b>{selectedLead?.Email || "-"}</b>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Notes + Files + Savings row like screenshot */}
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <div className="info-section">
                      <div className="section-header mb-3 d-flex justify-content-between align-items-center">
                        <h5 className="section-title mb-0">
                          Notes{" "}
                          {!isAddingNote && canEdit && (
                            <img
                              src={iconAddNotes}
                              alt="add"
                              className="edit-icon ms-2 icon-button-small icon-button-pointer"
                              onClick={() => {
                                setIsAddingNote((v) => {
                                  const next = !v;
                                  if (!next) resetToInitial(["Notes1"]);
                                  return next;
                                });
                              }}
                            />
                          )}
                        </h5>
                        {isAddingNote && (
                          <div className="rr-section-actions">
                            <button
                              type="button"
                              className="rr-section-action-btn rr-section-action-save"
                              onClick={() => {
                                if (loading || !canEdit) return;
                                isSavingNotesRef.current = true;
                                form.submit();
                              }}
                              disabled={loading || !canEdit}
                              title="Save"
                              aria-label="Save"
                            >
                              <SaveButton
                                isLoading={loading && isSavingNotesRef.current}
                              />
                            </button>
                            {!loading && (
                              <button
                                type="button"
                                className="rr-section-action-btn rr-section-action-cancel"
                                onClick={() => {
                                  resetToInitial(["Notes1"]);
                                  setIsAddingNote(false);
                                }}
                                title="Close"
                                aria-label="Close"
                              >
                                <CloseCircleIcon />
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {isAddingNote ? (
                        <Form.Item name="Notes1" style={{ marginBottom: 0 }}>
                          <AntdTextArea
                            autoSize={{ minRows: 3, maxRows: 10 }}
                            placeholder="Enter notes..."
                            disabled={!canEdit}
                          />
                        </Form.Item>
                      ) : (
                        <div className="field-value">
                          <b>{selectedLead?.Notes1 || "-"}</b>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-4 mb-3">
                    <div className="info-section rr-files-section">
                      <div className="section-header mb-3">
                        <h5 className="section-title mb-0">Files</h5>
                      </div>

                      {isLoadingAttachments ? (
                        <div className="rr-files-row">
                          {["pdf", "pdf", "pdf", "image", "image"].map(
                            (kind, i) => (
                              <div
                                key={`rr-file-skel-${i}`}
                                className="rr-file-tile rr-file-tile-skeleton"
                                aria-hidden="true"
                              >
                                <div className="rr-file-icon">
                                  {kind === "pdf" ? (
                                    <PdfIconSkeleton />
                                  ) : (
                                    <ImageIconSkeleton />
                                  )}
                                </div>
                                <div className="rr-file-name-skeleton" />
                              </div>
                            )
                          )}
                        </div>
                      ) : attachments.length === 0 ? (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="No files"
                        />
                      ) : (
                        <div className="rr-files-row">
                          {attachments
                            .slice(
                              0,
                              RATE_REVIEW_CONSTANTS.MAX_PROCESSING_STATEMENTS_FILES
                            )
                            .map((attachment, index) => {
                              const fileName =
                                attachment?.fileName ||
                                attachment?.file_name ||
                                attachment?.name ||
                                `File ${index + 1}`;

                              const attachmentId =
                                attachment?.id ||
                                attachment?.attachment_id ||
                                attachment?.attachmentId ||
                                attachment?.file_id ||
                                attachment?.fileId ||
                                "";

                              const fileType =
                                attachment?.fileType ||
                                attachment?.file_type ||
                                "";
                              const kind = getFileKind(fileName, fileType);

                              return (
                                <div
                                  key={attachmentId || `${fileName}-${index}`}
                                  className={`rr-file-tile rr-file-tile-${kind}`}
                                  title={fileName}
                                  onClick={() => handleFileClick(attachment)}
                                  style={{ cursor: "pointer" }}
                                >
                                  <div className="rr-file-icon">
                                    {kind === "pdf" ? (
                                      <PdfIcon />
                                    ) : kind === "image" ? (
                                      <ImageIcon />
                                    ) : (
                                      <ImageIcon />
                                    )}
                                  </div>

                                  <div className="rr-file-name-link">
                                    {fileName}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-4 mb-3">
                    <div className="info-section">
                      <div className="section-header mb-3">
                        <h5 className="section-title mb-0">Savings</h5>
                      </div>

                      <div className="rr-savings-row">
                        <Form.Item
                          name="Savings"
                          style={{ marginBottom: 0, flex: 1, minWidth: 0 }}
                        >
                          <Input
                            className="rr-savings-input"
                            placeholder="Savings"
                            disabled={!canEdit}
                          />
                        </Form.Item>

                        <button
                          type="button"
                          className="rr-save-icon-btn"
                          onClick={handleSavingsSave}
                          title="Save"
                          disabled={loading || !canEdit}
                        >
                          {/* Show loader when saving Savings, otherwise show icon */}
                          {loading && isSavingSavingsRef.current ? (
                            <div
                              style={{
                                width: "44px",
                                height: "44px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <div
                                className="simple-spinner"
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  border: "3px solid #e5f7ed",
                                  borderTop: "3px solid #10B981",
                                  borderRadius: "50%",
                                  animation: "spin 1s linear infinite",
                                }}
                              />
                            </div>
                          ) : (
                            /* Savings icon (green floppy) like attached reference */
                            <svg
                              key="rr-savings-icon-v5"
                              width="44"
                              height="44"
                              viewBox="0 0 64 64"
                              xmlns="http://www.w3.org/2000/svg"
                              aria-hidden="true"
                              preserveAspectRatio="xMidYMid meet"
                            >
                              {/* green floppy body with rounded corners + top-right diagonal */}
                              <path
                                d="M14 8h32l10 10v32c0 3.314-2.686 6-6 6H14c-3.314 0-6-2.686-6-6V14c0-3.314 2.686-6 6-6Z"
                                fill="currentColor"
                              />
                              <path d="M46 8v10h10L46 8Z" fill="currentColor" />

                              {/* white top window */}
                              <rect
                                x="20"
                                y="18"
                                width="24"
                                height="14"
                                rx="4"
                                fill="#ffffff"
                              />
                              {/* white circle */}
                              <circle cx="32" cy="44" r="10" fill="#ffffff" />
                            </svg>
                          )}
                        </button>
                      </div>

                      <div className="rr-schedule-checkbox">
                        <label>
                          <input
                            type="checkbox"
                            checked={
                              watchedLeadStatus === "Schedule a Call to discuss"
                            }
                            onChange={(e) => {
                              if (!canEdit) return;
                              const checked = e.target.checked;
                              if (checked) {
                                form.setFieldsValue({
                                  Lead_Status: "Schedule a Call to discuss",
                                });
                                return;
                              }

                              // Uncheck: revert to initial (or current lead status) without auto-submitting
                              const initial =
                                initialFormValuesRef.current || {};
                              const fallback =
                                initial?.Lead_Status ||
                                selectedLead?.Lead_Status ||
                                selectedLead?.status ||
                                "";
                              form.setFieldsValue({ Lead_Status: fallback });
                            }}
                            disabled={!canEdit}
                          />
                          Set Status to Schedule a Call
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Hidden fields still live in the form for validation/submission */}
                <div style={{ display: "none" }}>
                  <Form.Item name="First_Name">
                    <Input />
                  </Form.Item>
                  <Form.Item name="Last_Name">
                    <Input />
                  </Form.Item>
                  <Form.Item name="Company">
                    <Input />
                  </Form.Item>
                  <Form.Item name="Country_of_Formation">
                    <Input />
                  </Form.Item>
                  <Form.Item name="Lead_Status">
                    <Input />
                  </Form.Item>
                </div>
              </Form>
            )}
          </div>
        </div>
      </div>

      {/* File Viewer Modal */}
      <FileViewerModal
        viewingFile={viewingFile}
        onClose={handleCloseFileViewer}
      />
    </div>
  );
};

export default UpdateLeadModal;
